import { HeroUIProvider, ToastProvider } from "@heroui/react";

import "./App.css";
import Header from "../components/widgets/Header";
import { Route, Switch, useLocation, Redirect } from "wouter";
import HomePage from "../components/pages/app/HomePage/HomePage";
// import RequestPage from "src/components/pages/app/RequestPage/RequestPage";
import { Suspense, useEffect, useState } from "react";
import { firebaseApi } from "src/firebase-api";
import { useStore } from "src/store/store";
import DoctorPage from "src/components/pages/doctor/DoctorPage/DoctorPage";
import BrokerPage from "src/components/pages/broker/BrokerPage/BrokerPage";
import NotFoundPage from "src/components/pages/NotFoundPage/NotFoundPage";
import { TProfile, TRequest } from "@shared/entities";
import { ModalContainer } from "src/infra/ModalProvider/ModalContainer";
import { ProdBackendBanner } from "src/components/ui/ProdBackendBanner/ProdBackendBanner";
import { NewVersionBanner } from "src/components/ui/NewVersionBanner/NewVersionBanner";
import { useVersionCheck } from "src/hooks/useVersionCheck";
import { useAutoReloadOnNewVersion } from "src/hooks/useAutoReloadOnNewVersion";
import { lazyWithReload } from "src/utils/lazyWithReload";
import AdminRequestDetailsPage from "src/components/pages/admin/AdminRequestDetailsPage/AdminRequestDetailsPage";
import { analytics } from "src/infra/analytics/analytics";
import AdminCreateRequest from "src/components/pages/admin/AdminCreateRequest/AdminCreateRequest";
import AdminEditRequest from "src/components/pages/admin/AdminCreateRequest/AdminEditRequest";
import BrokerEditRequest from "src/components/pages/broker/BrokerEditRequest/BrokerEditRequest";
import ClientCreateRequest from "src/components/pages/app/ClientCreateRequest/ClientCreateRequest";
import ClientCreateRecommendationOnly from "src/components/pages/app/ClientCreateRecommendationOnly/ClientCreateRecommendationOnly";
import { ProtectedRoute } from "src/components/utils/ProtectedRoute";
import { useI18nReady } from "src/hooks/useI18nReady";
import {
	LoadingScreen,
	TranslationLoadingScreen,
	AuthLoadingScreen,
} from "src/components/ui/LoadingScreen/LoadingScreen";

const AdminPage = lazyWithReload(() => import("src/components/pages/admin/AdminPage/AdminPage"));
const DoctorTemplatesPage = lazyWithReload(
	() => import("src/components/pages/doctor/DoctorTemplatesPage/DoctorTemplatesPage")
);
const DoctorTemplateEditPage = lazyWithReload(
	() => import("src/components/pages/doctor/DoctorTemplateEditPage/DoctorTemplateEditPage")
);
const DoctorProfilePage = lazyWithReload(
	() => import("src/components/pages/doctor/DoctorProfilePage/DoctorProfilePage")
);
const AdminProductDetailPage = lazyWithReload(
	() => import("src/components/pages/admin/AdminPage/components/admin-product-detail-page")
);
const AdminPharmacyOrderDetailsPage = lazyWithReload(
	() =>
		import(
			"src/components/pages/admin/AdminPharmacyOrderDetailsPage/AdminPharmacyOrderDetailsPage"
		)
);
const ProductDetailPage = lazyWithReload(
	() => import("src/components/pages/app/ProductDetailPage/ProductDetailPage")
);
const RegisterPage = lazyWithReload(() => import("src/components/pages/auth/RegisterPage/RegisterPage"));
const LoginPage = lazyWithReload(() => import("src/components/pages/auth/LoginPage/LoginPage"));
const PharmacyCheckoutPage = lazyWithReload(
	() => import("src/components/pages/app/PharmacyCheckoutPage/PharmacyCheckoutPage")
);
const MyProfilePage = lazyWithReload(() => import("src/components/pages/app/MyProfilePage/MyProfilePage"));
const OrderSuccessPage = lazyWithReload(
	() => import("src/components/pages/app/OrderSuccessPage/OrderSuccessPage")
);
const WelcomePage = lazyWithReload(() => import("src/components/pages/app/WelcomePage/WelcomePage"));
const AdminClientProfilePage = lazyWithReload(
	() => import("src/components/pages/admin/AdminClientProfilePage/AdminClientProfilePage")
);
const PharmacyPage = lazyWithReload(() => import("src/components/pages/app/PharmacyPage/PharmacyPage"));
const CanaTermOfUsePage = lazyWithReload(
	() => import("src/components/pages/app/CanaTermOfUsePage/CanaTermOfUsePage")
);
const CanaPolicyPage = lazyWithReload(
	() => import("src/components/pages/app/CanaPolicyPage/CanaPolicyPage")
);
const PharmacyPolicyPage = lazyWithReload(
	() => import("src/components/pages/app/PharmacyPolicyPage/PharmacyPolicyPage")
);

function useProfileSubscribe() {
	const store = useStore();

	useEffect(() => {
		if (!store.user) {
			return store.storeApi.setProfile(null);
		}
		const unsubscribe = firebaseApi.firestore.subscribeDoc<TProfile>({
			collectionName: "profiles",
			id: store.user!.uid,
			callback(data) {
				store.storeApi.setProfile(data);
				if (data) {
					analytics.identify(data);
				}
			},
		});

		return unsubscribe;
	}, [store.user]);
}

function useActiveRequestsSubscribe() {
	const store = useStore();

	useEffect(() => {
		if (!store.user) {
			return store.storeApi.setActiveRequests([]);
		}

		// Only fetch active requests for client users (not admin or doctors)
		if (store.user.admin || store.user.issuingDoctor || store.user.recommendingDoctor) {
			return store.storeApi.setActiveRequests([]);
		}

		// Fetch active requests once when user logs in
		const fetchActiveRequests = async () => {
			try {
				const requests = await firebaseApi.firestore.list<TRequest>({
					collectionName: "requests",
					filter: [
						{ name: "userId", operator: "==", value: store.user!.uid },
						{
							name: "status",
							operator: "not-in",
							value: ["completed", "rejected"],
						},
					],
				});
				store.storeApi.setActiveRequests(requests);
			} catch (error) {
				console.error("Error fetching active requests:", error);
				store.storeApi.setActiveRequests([]);
			}
		};

		fetchActiveRequests();
	}, [store.user]);
}

function useTrackPageView() {
	const [location] = useLocation();

	useEffect(() => {
		console.log("PAGE_VIEW", location);
		analytics.track("PAGE_VIEW", { path: location });
	}, [location]);
}

function App() {
	const [, navigate] = useLocation();

	const store = useStore();
	const { ready: i18nReady, error: i18nError } = useI18nReady();

	const [authReady, setAuthReady] = useState(false);

	useEffect(() => {
		const unsubscribe = firebaseApi.auth.onUser(async (user) => {
			store.storeApi.setUser(user);
			unsubscribe();
			setAuthReady(true);
		});
	}, []);

	useProfileSubscribe();
	useActiveRequestsSubscribe();
	useTrackPageView();

	// Every tab checks for a newer deploy and moves onto it by itself at a safe
	// moment (useAutoReloadOnNewVersion). Admins also get the banner, since they
	// may stay on one screen long enough to want to reload sooner.
	const { updateAvailable, deployedBuildId } = useVersionCheck(true);
	useAutoReloadOnNewVersion(updateAvailable, deployedBuildId);

	// Show appropriate loading screen based on what's not ready
	if (!authReady && !i18nReady) {
		return <LoadingScreen message="מאתחל את האפליקציה..." />;
	}

	if (!authReady) {
		return <AuthLoadingScreen />;
	}

	if (!i18nReady) {
		return <TranslationLoadingScreen />;
	}

	// Show error screen if i18n failed to load
	if (i18nError) {
		return (
			<div className="p-8 w-full min-h-screen flex items-center justify-center">
				<div className="flex flex-col items-center max-w-md text-center">
					<div className="text-red-600 mb-4 text-xl font-semibold">שגיאה בטעינת התרגומים</div>
					<div className="text-sm text-gray-600 mb-6">{i18nError.message}</div>
					<button
						onClick={() => window.location.reload()}
						className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
					>
						נסה שוב
					</button>
				</div>
			</div>
		);
	}

	return (
		<HeroUIProvider navigate={navigate}>
			<ToastProvider />
			<ProdBackendBanner />
			{updateAvailable && store.user?.admin && <NewVersionBanner />}
			<ModalContainer>
				<Header />

				<Suspense fallback={<LoadingScreen />}>
					<Switch>
						{/* Public routes - accessible to everyone */}
						<Route path={"/"}>
							<HomePage />
						</Route>

						{/* Public welcome page */}
						<Route path={"/welcome"}>
							<WelcomePage />
						</Route>

						{/* Public PDF policy pages */}
						<Route path={"/policy/cana-terms"}>
							<CanaTermOfUsePage />
						</Route>
						<Route path={"/policy/cana-privacy"}>
							<CanaPolicyPage />
						</Route>
						<Route path={"/policy/pharmacy-privacy"}>
							<PharmacyPolicyPage />
						</Route>

						{/* Auth routes - only for non-authenticated users */}
						<Route path={"/register"}>
							{store.user ? <Redirect to="/" replace /> : <RegisterPage />}
						</Route>
						<Route path={"/login"}>
							{store.user ? <Redirect to="/" replace /> : <LoginPage />}
						</Route>

						{/* Authenticated user routes */}
						{/* Public: a payer may come back from Hyp without being signed in. */}
						<Route path={"/orderSuccess"}>
							<OrderSuccessPage />
						</Route>
						<ProtectedRoute allowedRoles={["user", "admin", "doctor"]} path={"/my_profile"}>
							<MyProfilePage />
						</ProtectedRoute>

						{/* User routes */}
						<ProtectedRoute allowedRoles={["user", "admin", "doctor"]} path={"/pharmacy"}>
							<PharmacyPage />
						</ProtectedRoute>
						<ProtectedRoute
							allowedRoles={["user", "admin", "doctor"]}
							path={"/pharmacy/products/:id"}
						>
							<ProductDetailPage />
						</ProtectedRoute>
						<ProtectedRoute
							allowedRoles={["user", "admin", "doctor"]}
							path={"/pharmacy/checkout"}
						>
							<PharmacyCheckoutPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["user"]} path={"/new_request"}>
							<ClientCreateRequest />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["user"]} path={"/new_recommendation"}>
							<ClientCreateRecommendationOnly />
						</ProtectedRoute>

						{/* Doctor routes */}
						<ProtectedRoute allowedRoles={["doctor"]} path={"/doctor"}>
							<DoctorPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["doctor"]} path={"/doctor/templates"}>
							<DoctorTemplatesPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["doctor"]} path={"/doctor/templates/new"}>
							<DoctorTemplateEditPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["doctor"]} path={"/doctor/templates/:id"}>
							<DoctorTemplateEditPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["doctor"]} path={"/doctor/profile"}>
							<DoctorProfilePage />
						</ProtectedRoute>

						{/* Broker routes */}
						<ProtectedRoute allowedRoles={["broker"]} path={"/broker"}>
							<BrokerPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["broker"]} path={"/broker/create-request"}>
							<AdminCreateRequest />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["broker"]} path={"/broker/requests/:id/edit"}>
							<BrokerEditRequest />
						</ProtectedRoute>

						{/* Admin routes */}
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/dashboard"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/requests"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/clients"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/crm/clients"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/crm/leads"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/crm/tasks"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/crm/automations"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/doctors"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/brokers"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/store/products"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/pharmacy/products"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/pharmacy/products/:id"}>
							<AdminProductDetailPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/pharmacy/orders"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/pharmacy/orders/:id"}>
							<AdminPharmacyOrderDetailsPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/analytics"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/settings"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/changelog"}>
							<AdminPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/new_request"}>
							<AdminCreateRequest />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin", "doctor"]} path={"/admin/requests/:id"}>
							<AdminRequestDetailsPage />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/requests/:id/edit"}>
							<AdminEditRequest />
						</ProtectedRoute>
						<ProtectedRoute allowedRoles={["admin"]} path={"/admin/client/:id"}>
							<AdminClientProfilePage />
						</ProtectedRoute>

						{/* 404 route */}
						<Route>
							<NotFoundPage />
						</Route>
					</Switch>
				</Suspense>
			</ModalContainer>
		</HeroUIProvider>
	);
}

export default App;
